import React, { useState, useEffect } from "react";
import { Descriptions, Button, Flex, Select, notification, Input, Modal, DatePicker, Form, Typography, Tabs, Tooltip, Checkbox } from "antd";
import { EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import isEqual from "lodash/isEqual";
import { InfoCircleOutlined } from '@ant-design/icons';

import axiosInstance from "../../../utilities/axios";
import ConditionSwitcher from "./ApplicabilityLogic";
import AmountOrder from "./AmountOrder/AmountOrder";
import { DISCOUNT_TYPES } from "./NewDiscountModal";

const { Title } = Typography;

const formatLogicString = (raw) => {
  if (!raw || typeof raw !== 'string') return raw;
  try {
    let indent = 0;
    const indentStr = (level) => ' '.repeat(level);
    let out = '';

    for (let i = 0; i < raw.length; i++) {
      const ch = raw[i];
      if (ch === '(') {
        out += '(' + '\n' + indentStr(++indent);
      } else if (ch === ')') {
        out += '\n' + indentStr(--indent) + ')';
      } else if (ch === ',') {
        out += ',' + '\n' + indentStr(indent);
      } else {
        out += ch;
      }
    }

    return out.replace(/^\s*\n/, '').replace(/\n\s*$/, '');
  } catch (e) {
    return raw;
  }
};

const DiscountModal = ({ discount, open, onClose, onChange, path }) => {
  const [form] = Form.useForm();
  const allValues = Form.useWatch([], form);

  const [editMode, setEditMode] = useState(false);
  const [types, setTypes] = useState([]);
  const [activeKey, setActiveKey] = useState("0");

  const [detailedDiscount, setDetailedDiscount] = useState({});
  const discountsToCheck = editMode ? allValues?.discounts : detailedDiscount?.discounts;
  const hasProductPriceDiscount = discountsToCheck?.some((subDiscount) =>
    subDiscount?.amounts?.some((amount) => amount?.valueType === "product-price")
  ) ?? false;

  useEffect(() => {
    if (discount?.id === undefined) return;

    axiosInstance
      .get(`/discounts/${discount.id}`)
      .then((response) => {
        setTypes(response.data.map(d => d.valueType));
        setDetailedDiscount({
          discountName: discount?.discountName,
          discountType: discount?.discountType,
          hideDiscountName: discount?.hideDiscountName,
          effectiveDate: discount?.effectiveDate ? dayjs(discount.effectiveDate) : null,
          expirationDate: discount?.expirationDate ? dayjs(discount.expirationDate) : null,
          status: discount?.status,
          incompatibleDiscountTypes: discount?.incompatibleDiscountTypes || [],
          discounts: response.data || [],
        });

        form.setFieldsValue({
          discountName: discount?.discountName,
          discountType: discount?.discountType,
          hideDiscountName: discount?.hideDiscountName,
          effectiveDate: discount?.effectiveDate ? dayjs(discount.effectiveDate) : null,
          expirationDate: discount?.expirationDate ? dayjs(discount.expirationDate) : null,
          status: discount?.status,
          incompatibleDiscountTypes: discount?.incompatibleDiscountTypes || [],
          discounts: response.data || [],
        });
      })
      .catch((error) => {
        notification.error({
          message: "Error",
          description: error.message,
        });
      });
  }, [discount]);

  const saveChanges = (values) => {
    const updatedDiscounts = values.discounts.map((item, idx) => {
      const original = detailedDiscount.discounts?.[idx] || {};
      if (!isEqual(item.logic, original.logic)) {
        return { ...item, logic: item.logic };
      } else {
        const { logic, ...rest } = item;
        return rest;
      }
    });

    const payload = {
      ...values,
      discounts: updatedDiscounts
    };

    axiosInstance
      .put(`/discounts/${discount?.id}`, payload)
      .then((response) => {
        onChange();
        setEditMode(false);
        onClose();
      })
      .catch((error) => {
        notification.error({
          message: "Error",
          description: error.message,
        });
      });
  };

  const handleClose = () => {
    setEditMode(false);
    onClose();
    form.resetFields();
    setActiveKey("0");
    setDetailedDiscount({});
  };

  return (
    <Modal
      title="Discount Details"
      open={open}
      width={800}
      onCancel={handleClose}
      onClose={handleClose}
      footer={
        <Button
          key="close"
          onClick={handleClose}
        >
          Close
        </Button>
      }
      styles={{ content: { overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' } }}
    >
      <Form
        form={form}
        layout="vertical"
        name="discountForm"
        initialValues={detailedDiscount}
        onFinish={saveChanges}
      >
        <Descriptions
          bordered
          column={4}
          style={{ marginBottom: "24px" }}
        >
          <Descriptions.Item
            label={path === "/other-discounts" ? "Discount Name" : path === "/affinity-partners" ? "Affinity Name" : "Discount Reason Name"}
            span={4}
          >
            {editMode ? (
              <Form.Item
                name="discountName"
                rules={[{ required: true, message: "Please input the discount name!" }]}
                style={{ marginBottom: 0 }}
              >
                <Input
                  style={{ width: "100%" }}
                />
              </Form.Item>
            ) : (
              detailedDiscount?.discountName
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Discount Type" span={2}>
            {editMode ? (
              <Form.Item
                name="discountType"
                rules={[{ required: true, message: "Please select a discount type!" }]}
                style={{ marginBottom: 0 }}
              >
                <Select
                  style={{ width: "100%" }}
                >
                  <Select.Option value="everyday-low">Everyday Low</Select.Option>
                  <Select.Option value="matrix">Matrix</Select.Option>
                  <Select.Option value="subsidy">Subsidy</Select.Option>
                  <Select.Option value="marketing">Discount Reason</Select.Option>
                  <Select.Option value="affinity">Affinity Partner</Select.Option>
                </Select>
              </Form.Item>
            ) : (
              detailedDiscount?.discountType ? detailedDiscount.discountType.charAt(0).toUpperCase() + detailedDiscount.discountType.slice(1) : "Unknown"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Status" span={2}>
            {detailedDiscount?.status ? detailedDiscount?.status.charAt(0).toUpperCase() + detailedDiscount?.status.slice(1) : "Unknown"}
          </Descriptions.Item>
          {hasProductPriceDiscount && (
            <Descriptions.Item
              label={
                <span style={{ whiteSpace: "nowrap" }}>
                  Hide Discount Name
                  <Tooltip title="Hide the discount name from being displayed on estimates for product price discounts.">
                    <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
                  </Tooltip>
                </span>
              }
              span={4}
            >
              {editMode ? (
                <Form.Item
                  name="hideDiscountName"
                  valuePropName="checked"
                  preserve={false}
                  style={{ marginBottom: 0 }}
                >
                  <Checkbox />
                </Form.Item>
              ) : (
                detailedDiscount?.hideDiscountName ? "Yes" : "No"
              )}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Effective Date" span={2}>
            {editMode ? (
              <Form.Item
                name="effectiveDate"
                rules={[{ required: true, message: "Please select an effective date!" }]}
                style={{ marginBottom: 0 }}
              >
                <DatePicker
                  style={{ width: "100%" }}
                />
              </Form.Item>
            ) : (
              dayjs(detailedDiscount?.effectiveDate).format("YYYY-MM-DD")
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Expiration Date" span={2}>
            {editMode ? (
              <Form.Item
                name="expirationDate"
                style={{ marginBottom: 0 }}
              >
                <DatePicker
                  style={{ width: "100%" }}
                />
              </Form.Item>
            ) : (
              detailedDiscount?.expirationDate ? dayjs(detailedDiscount.expirationDate).format("YYYY-MM-DD") : null
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Incompatible Discount Types" span={4}>
            {editMode ? (
              <Form.Item
                name="incompatibleDiscountTypes"
                style={{ marginBottom: 0 }}
              >
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder="Select Incompatible Discount Types"
                >
                  {DISCOUNT_TYPES.filter(type => type.path !== path).map((type) => (
                    <Select.Option key={type.value} value={type.value}>
                      {type.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            ) : (
              detailedDiscount?.incompatibleDiscountTypes?.length > 0 ? detailedDiscount.incompatibleDiscountTypes.map(r => r.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')).join(", ") : "None"
            )}
          </Descriptions.Item>
        </Descriptions>
        {editMode ? (
          <Form.List name="discounts">
            {(fields, { add, remove }) => {
              const canRemove = fields.length > 1;
              return (
                <Tabs
                  type="editable-card"
                  activeKey={activeKey}
                  onChange={setActiveKey}
                  onEdit={(targetKey, action) => {
                    if (action === 'add') {
                      const newKey = `${fields.length}`;
                      add({ amounts: [{}] });
                      setActiveKey(newKey);
                    } else if (action === 'remove' && canRemove) {
                      remove(Number(targetKey));
                      setActiveKey(fields.length > 1 ? `${fields[0].name}` : "0");
                    }
                  }}
                  items={fields.map(({ key, name, ...restField }, idx) => {
                    const dis = detailedDiscount.discounts?.[idx] || {};
                    return {
                      label: dis.subDiscountName ? (
                        <Tooltip overlayStyle={{ maxWidth: 1000 }} title={<pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{formatLogicString(dis.logicString)}</pre>}>
                          <span style={{ display: 'inline-block', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>{dis.subDiscountName}</span>
                        </Tooltip>
                      ) : `Discount ${idx + 1}`,
                      key: `${name}`,
                      closable: canRemove,
                      children: (
                        <>
                          <Form.Item
                            {...restField}
                            name={[name, 'id']}
                            hidden
                          >
                            <Input disabled />
                          </Form.Item>

                          <Form.Item
                            {...restField}
                            label="Sub Discount Name"
                            name={[name, 'subDiscountName']}
                            rules={[{ required: true, message: "Please input a sub discount name" }]}
                          >
                            <Input placeholder="Enter Sub Discount Name" />
                          </Form.Item>

                          <Flex style={{ marginBottom: 9 }} align="center">
                            <Title level={5} style={{ margin: 0 }}>
                              Discount Amounts
                            </Title>
                            <Tooltip title="You can add multiple discount amounts for each discount. The order of these discount amounts will determine the order in which they are applied.">
                              <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
                            </Tooltip>
                          </Flex>
                          <AmountOrder key={name} form={form} path={path} outerName={name} />

                          <Title level={5} style={{ marginTop: "24px" }}>
                            Applicability Rules
                          </Title>
                          <Form.Item name={[name, "logic"]} style={{ marginBottom: 0 }}>
                            <ConditionSwitcher
                              value={discount[idx]?.logic}
                            />
                          </Form.Item>
                        </>
                      )
                    };
                  })}
                />
              );
            }}
          </Form.List>
        ) : (
          <Tabs
            type="card"
            activeKey={activeKey}
            onChange={setActiveKey}
            items={detailedDiscount.discounts?.map((dis, idx) => {
              return {
                label: dis.subDiscountName ? (
                  <Tooltip overlayStyle={{ maxWidth: 1000 }} title={<pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{formatLogicString(dis.logicString)}</pre>}>
                    <span style={{ display: 'inline-block', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>{dis.subDiscountName}</span>
                  </Tooltip>
                ) : `Discount ${idx + 1}`,
                key: `${idx}`,
                children: (
                  <>
                    <Flex style={{ marginBottom: 9 }} align="center">
                      <Title level={5} style={{ margin: 0 }}>
                        Discount Amounts
                      </Title>
                      <Tooltip title="You can add multiple discount amounts for each discount. The order of these discount amounts will determine the order in which they are applied.">
                        <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
                      </Tooltip>
                    </Flex>
                    {dis.amounts.map((amount) => (
                      <Descriptions
                        bordered
                        column={2}
                        style={{ marginBottom: 16 }}
                        styles={{
                          label: {
                            width: '50%'
                          }
                        }}
                      >
                        <Descriptions.Item label="Value Type" span={2}>
                          {amount.valueType === "percentage" ? (
                            <span>Percentage</span>
                          ) : amount.valueType === "fixed" ? (
                            <span>Fixed</span>
                          ) : (
                            <span>Product Price</span>
                          )}
                        </Descriptions.Item>
                        <Descriptions.Item label="Value" span={2}>
                          {amount.valueType === "percentage" ? `${amount?.discountValue}%` : `$${amount?.discountValue?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                        </Descriptions.Item>
                        {(path === "/subsidies" || path === "/discount-reasons") && (
                          <>
                            <Descriptions.Item label="Discount Value is Maximum" span={2}>
                              {amount.upTo ? "Yes" : "No"}
                            </Descriptions.Item>
                            {(amount.valueType === "fixed" || amount.valueType === "product-price") && (
                              <Descriptions.Item label="Quantity Invariant" span={2}>
                                {amount.quantityInvariant ? "Yes" : "No"}
                              </Descriptions.Item>
                            )}
                          </>
                        )}
                      </Descriptions>
                    ))}
                  </>
                )
              };
            })}
          />
        )}
        <Flex
          justify="flex-end"
          style={{ marginTop: "10px", marginBottom: "0px"}}
        >
          <Button
            key="save"
            htmlType="submit"
            type="primary"
            disabled={
              editMode &&
              (allValues.discountName === detailedDiscount.discountName &&
                allValues.discountType === detailedDiscount.discountType &&
                allValues.effectiveDate?.isSame(detailedDiscount.effectiveDate) &&
                (allValues.expirationDate === null && detailedDiscount?.expirationDate === null ?
                  true : allValues.expirationDate?.isSame(detailedDiscount?.expirationDate)) &&
                allValues.status === detailedDiscount.status &&
                isEqual(allValues.discounts, detailedDiscount.discounts))
            }
            style={{ display: editMode ? "block" : "none" }}
            >
              Save Changes
            </Button>
            {discount?.discountName !== "Price Match" && (
              <Button
                key="edit"
                type={editMode ? "dashed" : "text"}
                icon={<EditOutlined />}
                onClick={() => {
                  if (editMode) {
                    form.resetFields();
                    setTypes(detailedDiscount?.discounts.map(d => d.valueType));
                  }
                  setEditMode(!editMode);
                }}
                style={{ marginLeft: "5px", display: detailedDiscount?.status === "expired" ? "none" : "block" }}
              />
            )}
        </Flex>
      </Form>
    </Modal>
  );
};

export default DiscountModal;